/**
 * Daggerheart PC Hope pool size.
 * Starting Hope is 2; the pool is hope_max (typically 6).
 */

export const STARTING_PC_HOPE = 2;
export const DEFAULT_PC_HOPE_MAX = 6;

function asPositiveInt(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    const n = Math.trunc(value);
    return n >= 1 ? n : null;
  }
  if (typeof value === "string") {
    const pair = parseHopeCurrentMax(value);
    if (pair?.max != null) return pair.max;
    const n = parseInt(value.replace(/[^0-9-]/g, ""), 10);
    return Number.isFinite(n) && n >= 1 ? n : null;
  }
  return null;
}

function asNonNegInt(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    const n = Math.trunc(value);
    return n >= 0 ? n : null;
  }
  if (typeof value === "string") {
    const pair = parseHopeCurrentMax(value);
    if (pair?.current != null) return pair.current;
    const n = parseInt(value.replace(/[^0-9-]/g, ""), 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  return null;
}

/** Parse "2 / 6" or "2/6" style Hope values. */
export function parseHopeCurrentMax(
  value: unknown,
): { current: number; max: number | null } | null {
  if (typeof value !== "string") return null;
  const m = /^\s*(\d+)\s*\/\s*(\d+)\s*$/.exec(value);
  if (!m) return null;
  const current = parseInt(m[1]!, 10);
  const max = parseInt(m[2]!, 10);
  if (!Number.isFinite(current) || !Number.isFinite(max) || max < 1) return null;
  return { current, max };
}

/**
 * SRD classes all use a Hope maximum of 6.
 * Kept as a function so a class override can be added later without sheet changes.
 */
export function classHopeMax(_className?: string): number {
  return DEFAULT_PC_HOPE_MAX;
}

/**
 * Box count for the Hope tracker.
 * `hope_max` is authoritative. Otherwise class default (6).
 * `max_hope` is an alias only when it looks like a pool size, not starting Hope (2).
 */
export function resolvePcHopeMax(
  creature: Record<string, unknown> | null | undefined,
): number {
  const bag = creature ?? {};
  const fromHopeMax = asPositiveInt(
    bag.hope_max ?? bag.hopeMax ?? bag["hope-max"],
  );
  if (fromHopeMax != null) return fromHopeMax;

  const pair = parseHopeCurrentMax(bag.hope);
  if (pair?.max != null) return pair.max;

  const alias = asPositiveInt(bag.max_hope ?? bag.maxHope);
  if (alias != null && alias >= DEFAULT_PC_HOPE_MAX) return alias;
  if (alias != null && alias > STARTING_PC_HOPE) return alias;

  const className =
    typeof bag.class === "string"
      ? bag.class
      : typeof bag.character_class === "string"
        ? bag.character_class
        : undefined;
  return classHopeMax(className);
}

export function resolvePcHopeCurrent(
  creature: Record<string, unknown> | null | undefined,
  maxHope = resolvePcHopeMax(creature),
): number {
  const bag = creature ?? {};
  const pair = parseHopeCurrentMax(bag.hope);
  const raw =
    asNonNegInt(typeof bag.hope === "number" ? bag.hope : null) ??
    pair?.current ??
    STARTING_PC_HOPE;
  return Math.min(Math.max(0, raw), maxHope);
}
